import { UserRole, UserStatus, type ServiceProvider } from '../types/firestore';

type AccountStatusValue = UserStatus | 'approved' | 'suspended' | string | null | undefined;
type RoleValue = UserRole | 'serwisant' | string | null | undefined;

export interface AccessClaims {
  [key: string]: unknown;
  isAdmin?: boolean;
  isServiceAdmin?: boolean;
  isServiceProvider?: boolean;
}

export interface AccessProfile {
  role?: RoleValue;
  status?: AccountStatusValue;
}

export type CommunityServiceProviderState = Pick<
  Partial<ServiceProvider>,
  'verificationStatus' | 'isVerified' | 'isTestData'
>;

const normalizeRole = (role?: RoleValue): UserRole | null => {
  if (!role) return null;
  const normalizedRoleValue = String(role).trim().toLowerCase();

  if (normalizedRoleValue === UserRole.CLIENT) return UserRole.CLIENT;
  if (normalizedRoleValue === UserRole.ADMIN) return UserRole.ADMIN;
  if (
    normalizedRoleValue === UserRole.SERVICE_ADMIN.toLowerCase() ||
    normalizedRoleValue === 'serwisant'
  ) {
    return UserRole.SERVICE_ADMIN;
  }

  return null;
};

const normalizeStatus = (status?: AccountStatusValue): UserStatus | null => {
  if (!status) return null;
  if (status === UserStatus.PENDING) return UserStatus.PENDING;
  if (status === UserStatus.PENDING_VERIFICATION) return UserStatus.PENDING_VERIFICATION;
  if (status === UserStatus.ACTIVE || status === 'approved') {
    return UserStatus.ACTIVE;
  }
  if (status === UserStatus.SUSPENDED || status === 'suspended') {
    return UserStatus.SUSPENDED;
  }
  if (status === UserStatus.REJECTED) return UserStatus.REJECTED;
  return null;
};

const getBaseProfileRole = (profile?: AccessProfile | null): UserRole | null => {
  return normalizeRole(profile?.role);
};

export const getUserRoleIdentity = (profile?: AccessProfile | null): UserRole | null => {
  return getBaseProfileRole(profile);
};

export const getUserRole = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): UserRole | null => {
  // Effective role for product UI logic:
  // - base role comes from users/{uid}
  // - admin capability is claim-first and must not survive revoke as "effective admin"
  const roleFromProfile = getBaseProfileRole(profile);
  if (!roleFromProfile) return null;
  if (roleFromProfile === UserRole.ADMIN) {
    return claims?.isAdmin === true ? UserRole.ADMIN : UserRole.CLIENT;
  }
  if (roleFromProfile === UserRole.SERVICE_ADMIN) {
    return UserRole.SERVICE_ADMIN;
  }
  if (roleFromProfile === UserRole.CLIENT) {
    return UserRole.CLIENT;
  }
  return null;
};

export const getUserStatus = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): UserStatus | null => {
  const normalizedStatus = normalizeStatus(profile?.status);
  if (normalizedStatus) return normalizedStatus;
  return null;
};

export const isClient = (profile?: AccessProfile | null, claims?: AccessClaims | null): boolean =>
  getUserRole(profile, claims) === UserRole.CLIENT;

export const isClientOrAdmin = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => {
  const role = getUserRole(profile, claims);
  return role === UserRole.CLIENT || role === UserRole.ADMIN;
};

export const isAdmin = (profile?: AccessProfile | null, claims?: AccessClaims | null): boolean =>
  claims?.isAdmin === true;

export const isServiceAdmin = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => getUserRole(profile, claims) === UserRole.SERVICE_ADMIN;

const hasApprovedServiceClaim = (claims?: AccessClaims | null): boolean =>
  claims?.isServiceAdmin === true || claims?.isServiceProvider === true;

export const canAccessServiceModule = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => isServiceAdmin(profile, claims);

export const isApprovedServiceAdmin = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean =>
  isServiceAdmin(profile, claims) &&
  getUserStatus(profile, claims) === UserStatus.ACTIVE &&
  hasApprovedServiceClaim(claims);

export const isActiveAdmin = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => isAdmin(profile, claims) && getUserStatus(profile, claims) === UserStatus.ACTIVE;

export const isActiveCommunityAdmin = isActiveAdmin;

export const canLocallyUseProductFeedbackReporter = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => {
  const role = getUserRoleIdentity(profile);
  if (getUserStatus(profile, claims) !== UserStatus.ACTIVE) return false;
  if (role === UserRole.CLIENT) return true;
  if (role === UserRole.SERVICE_ADMIN) return hasApprovedServiceClaim(claims);
  if (role === UserRole.ADMIN) return claims?.isAdmin === true;
  return false;
};

export const isApprovedCommunityServiceAdmin = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null,
  provider?: CommunityServiceProviderState | null
): boolean =>
  isApprovedServiceAdmin(profile, claims) &&
  provider?.verificationStatus === 'approved' &&
  provider.isVerified === true &&
  provider.isTestData !== true;

export const canAccessServiceDashboard = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => isApprovedServiceAdmin(profile, claims);

export const isAccountSuspended = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => getUserStatus(profile, claims) === UserStatus.SUSPENDED;

export const canSubmitRepairRequest = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => {
  if (!isClientOrAdmin(profile, claims)) {
    return false;
  }
  return getUserStatus(profile, claims) === UserStatus.ACTIVE;
};

export const canAccessSharedRequestsModule = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => {
  const role = getUserRole(profile, claims);
  if (!role) {
    return false;
  }
  const isRoleEligible =
    role === UserRole.CLIENT || role === UserRole.ADMIN || role === UserRole.SERVICE_ADMIN;
  if (!isRoleEligible) {
    return false;
  }
  return getUserStatus(profile, claims) === UserStatus.ACTIVE;
};

export const canCreateRepairRequestAsRequester = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null
): boolean => {
  const role = getUserRole(profile, claims);
  if (!role) {
    return false;
  }
  const isRoleEligible =
    role === UserRole.CLIENT || role === UserRole.ADMIN || role === UserRole.SERVICE_ADMIN;
  if (!isRoleEligible) {
    return false;
  }
  return getUserStatus(profile, claims) === UserStatus.ACTIVE;
};

export const isOwnServiceProviderTarget = (
  authUid?: string | null,
  targetServiceProviderId?: string | null
): boolean => {
  const normalizedAuthUid = (authUid || '').trim();
  const normalizedTargetId = (targetServiceProviderId || '').trim();
  if (!normalizedAuthUid || !normalizedTargetId) {
    return false;
  }
  return normalizedAuthUid === normalizedTargetId;
};

export const canCreateRepairRequestForServiceProvider = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null,
  authUid?: string | null,
  targetServiceProviderId?: string | null
): boolean => {
  const normalizedTargetId = (targetServiceProviderId || '').trim();
  if (!normalizedTargetId) {
    return false;
  }

  if (canSubmitRepairRequest(profile, claims)) {
    return true;
  }

  if (!isApprovedServiceAdmin(profile, claims)) {
    return false;
  }

  const normalizedAuthUid = (authUid || '').trim();
  if (!normalizedAuthUid) {
    return false;
  }

  return normalizedAuthUid !== normalizedTargetId;
};

export const canShowRepairRequestCtaForServiceProvider = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null,
  authUid?: string | null,
  targetServiceProviderId?: string | null,
  isTestData = false
): boolean => {
  const normalizedTargetId = (targetServiceProviderId || '').trim();
  if (!normalizedTargetId) {
    return false;
  }

  if (isTestData) {
    return false;
  }

  if (isOwnServiceProviderTarget(authUid, normalizedTargetId)) {
    return false;
  }

  const normalizedAuthUid = (authUid || '').trim();
  if (!normalizedAuthUid) {
    return true;
  }

  return canCreateRepairRequestForServiceProvider(
    profile,
    claims,
    normalizedAuthUid,
    normalizedTargetId
  );
};
